'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertBody, Button, Heading, TextField, TextPassage } from '@petsmart-ui/sparky';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Controller, type FieldErrors, FormProvider, useForm } from 'react-hook-form';

import { dataLayerPush, trackError } from '@libs/analytics';
import { getOrder } from '@libs/api-proxy-client';
import { logSentryError, SentryAcpOrderSearchErrors as SentryErrors, useUserSession } from '@libs/common-next';

import { OrderSearchFormSchema } from '@/schemas/OrderSearch.schema';

import type { OrderSearchFormValues } from './types';

import './OrderSearchPage.scss';

const className = 'order-search';
const formClassname = 'order-search-form';

const initialFormValues = {
    orderNumber: '',
    email: '',
};

export function OrderSearchPage() {
    const router = useRouter();
    const { isAuthenticated } = useUserSession();
    const [isHydrated, setIsHydrated] = useState<boolean>(false);
    const [serverError, setServerError] = useState<{
        message: string;
        description: string;
    } | null>(null);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    const form = useForm<OrderSearchFormValues>({
        defaultValues: initialFormValues,
        resolver: zodResolver(OrderSearchFormSchema),
        mode: 'onSubmit',
    });
    const {
        handleSubmit,
        register,
        control,
        formState: { errors, touchedFields, isSubmitting, isSubmitted },
        getValues,
    } = form;

    function getOrderDetailsUrl(values: OrderSearchFormValues) {
        const encodedEmail = encodeURIComponent(values.email);
        return `/account/order-details/${values.orderNumber}?email=${encodedEmail}`;
    }

    async function validateOrder({
        orderNumber,
        email,
    }: OrderSearchFormValues): Promise<true | { message: string; description: string }> {
        try {
            const response = await getOrder({
                orderId: orderNumber,
                email,
            });

            if (!response || ('status' in response && response.status !== 200)) {
                trackError('track order status', 'order not found');
                return {
                    message: 'Order not found',
                    description: 'Please confirm correct order information was entered',
                };
            }

            return true;
        } catch {
            const error = {
                message: 'An unexpected error occured',
                description: 'Please retry your request later',
            };
            logSentryError(error, SentryErrors.OrderSearchPage.ValidateOrderFailed);
            return error;
        }
    }

    async function handleFormSubmit(values: OrderSearchFormValues) {
        const orderValidation = await validateOrder(values);
        if (orderValidation !== true) {
            setServerError(orderValidation);
        } else {
            dataLayerPush({ event: 'track_order_status' });
            setServerError(null);
            router.push(getOrderDetailsUrl(values));
        }
    }

    function handleValidationError(errors: FieldErrors<OrderSearchFormValues>) {
        const { orderNumber, email } = getValues();
        const errorMessage =
            !orderNumber && !email ? 'empty form' : (errors.email?.message ?? errors.orderNumber?.message ?? '');
        trackError('track order status', errorMessage);
    }

    return (
        <div className={className}>
            <FormProvider {...form}>
                <form
                    noValidate
                    className={formClassname}
                    onSubmit={handleSubmit(handleFormSubmit, handleValidationError)}
                >
                    <div className={`${formClassname}__header`}>
                        <Heading tagName="h1" size="title" className={`${formClassname}__heading`}>
                            Track your order
                        </Heading>
                        {serverError && (
                            <Alert
                                variant="error"
                                iconName="exclamation-circle-fill"
                                className={`${formClassname}_alert`}
                                role="alert"
                            >
                                <AlertBody>
                                    <TextPassage className="error-passage">
                                        <strong>{serverError.message} </strong>
                                        {serverError.description}
                                    </TextPassage>
                                </AlertBody>
                            </Alert>
                        )}
                        <TextPassage className={`${formClassname}__subheading`} align="center">
                            Please provide your order number and email.
                        </TextPassage>
                    </div>
                    <div className={`${formClassname}__body`}>
                        <Controller
                            name="orderNumber"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    data-cs-mask
                                    label="Order number"
                                    type="text"
                                    inputMode="numeric"
                                    required
                                    {...field}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        if (/^\d*$/.test(e.target.value)) {
                                            field.onChange(e);
                                        }
                                    }}
                                    isError={(touchedFields.orderNumber || isSubmitted) && !!errors.orderNumber}
                                    fieldNote={
                                        (touchedFields.orderNumber || isSubmitted) && errors.orderNumber ? (
                                            <span data-testid="orderNumber-error">{errors.orderNumber.message}</span>
                                        ) : undefined
                                    }
                                />
                            )}
                        />
                        <TextField
                            data-cs-mask
                            label="Email address"
                            type="email"
                            required
                            {...register('email')}
                            isError={(touchedFields.email || isSubmitted) && !!errors.email}
                            fieldNote={
                                (touchedFields.email || isSubmitted) && errors.email ? (
                                    <span data-testid="email-error">{errors.email.message}</span>
                                ) : undefined
                            }
                        />
                    </div>
                    <div className={`${formClassname}__footer`}>
                        <Button
                            size="lg"
                            variant="primary"
                            text="Find my order"
                            fullWidth
                            className={`${formClassname}__submit`}
                            type="submit"
                            loading={isSubmitting}
                            disabled={!isHydrated || isSubmitting}
                        />
                    </div>
                    {isHydrated ? (
                        <TextPassage align="center" className={`${formClassname}__copy`}>
                            {isAuthenticated ? (
                                <>
                                    <span>{`Want to view all your orders? `}</span>
                                    <Link href="/account/order-history">View order history</Link>
                                </>
                            ) : (
                                <>
                                    <span>{`Have an account? `}</span>
                                    <Link target="_blank" href="/sign-in">
                                        Sign in here
                                    </Link>
                                </>
                            )}
                        </TextPassage>
                    ) : null}
                </form>
            </FormProvider>
        </div>
    );
}
